import { CARRIERS } from '../ShipmentForm/constants';
import {
  AdditionalPartField, AdditionalPartValues, FieldName, Part, PartUsage, PartEntry, PartV3,
  PartsProviderInputsValue,
  USAGE_CODES as USAGE_CODE_ENUM,
} from './types.d';
import { FIELD_ORDER, NEW_PART_EXCLUDED_FIELDS } from './constants';

export { getWorkorderStatusId } from '../utils/partsUtils';

export const RETURN_INSTRUCTION_OPTIONS_RETURNABLE = [
  { label: 'Return the part within 24 hours', value: 'Return within 24 hours' },
  { label: 'Return the part within 48 hours', value: 'Return within 48 hours' },
  { label: 'Return the part within 3 days', value: 'Return within 3 days' },
  { label: 'Return the part within 5 days', value: 'Return within 5 days' },
];

export const RETURN_INSTRUCTION_OPTIONS_NON_RETURNABLE = [
  { label: 'Leave the part on site', value: 'Leave part on site' },
  { label: 'Dispose of the part (reuse, recycle, or discard)', value: 'Dispose part' },
];

export const SELECT_OPTIONS = [{
  label: 'Yes',
  value: 'Yes',
}, {
  label: 'No',
  value: 'No',
}, {
  label: '',
  value: '',
}];

export const RETAINED_BY_CUSTOMER_OPTIONS = [
  {
    label: 'Yes',
    value: '1',
  },
  {
    label: 'No',
    value: '0',
  },
];

export const USAGE_CODES = [
  'Used',
  'Not used',
  'Not received',
  'Does not work',
  'Damaged in transit',
  'Wrong part',
];

export const getInitialRetainedByCustomer = (
  customerRetained: string,
): string => {
  if (!customerRetained) {
    return '';
  }
  if (customerRetained === 'Yes') {
    return '1';
  }
  return '0';
};

// Shared by the Usage form and validateAdditionalFields so both stay in sync.
// "Not received" means the part never showed up, so none of serial/internal/RMA
// number apply — hide the whole additional-fields section for the new part too.
export const getNewPartExcludedFields = (usageCode: string | undefined): FieldName[] => (
  usageCode === USAGE_CODE_ENUM.NOT_RECEIVED
    ? FIELD_ORDER
    : NEW_PART_EXCLUDED_FIELDS
);

// A field configured in `additional_part_fields` is required by presence alone —
// the buyer checking it in PartsProviderSettings is the "required" signal, there's
// no separate flag. The one exception is "Not received", which needs none of them
// since the part never showed up to be identified.
export const isAdditionalFieldRequired = (
  _fieldName: FieldName,
  usageCode: string | undefined,
): boolean => usageCode !== USAGE_CODE_ENUM.NOT_RECEIVED;

// Returns true when all required additional fields are filled.
export const validateAdditionalFields = (
  fields: AdditionalPartField[] | undefined,
  values: AdditionalPartValues | undefined,
  usageCode: string | undefined,
): boolean => {
  const fieldList = fields || [];
  const isNotReceived = usageCode === USAGE_CODE_ENUM.NOT_RECEIVED;

  const newPartExcludedFields = getNewPartExcludedFields(usageCode);
  const newPartBlocking = fieldList
    .filter((f) => isAdditionalFieldRequired(f.field_name, usageCode)
      && !newPartExcludedFields.includes(f.field_name))
    .some((f) => !String(values?.new?.[f.field_name] ?? '').trim());
  if (newPartBlocking) return false;

  // "Not received" has no Defective Part section, so its fields shouldn't block.
  if (usageCode && !isNotReceived) {
    const defectiveBlocking = fieldList
      .filter((f) => isAdditionalFieldRequired(f.field_name, usageCode))
      .some((f) => !String(values?.defective?.[f.field_name] ?? '').trim());
    if (defectiveBlocking) return false;
  }

  return true;
};

export const getUsageHeader = (
  externalId = '',
  externalNumber = '',
  description = '',
): string => {
  if (!externalId && !externalNumber && !description) {
    return '-';
  }
  return `${externalId} - ${externalNumber} - ${description}`;
};

export const isPartDraftValid = (part: Part): boolean => {
  if (
    !part?.externalId?.trim() ||
    !part?.externalNumber?.trim() ||
    !part?.description?.trim() ||
    !part?.returnable?.trim() ||
    !part?.trackingNumber?.trim() ||
    !part?.returnInstruction
  ) {
    return false;
  }

  if (!part?.carrier || (part.carrier === 'other' && !part?.carrierOther?.trim())) {
    return false;
  }

  return true;
};

export const formatReturnCell = (part: Part): string => {
  if (!part?.returnable) {
    return '-';
  }

  const instruction = part?.returnInstruction || '';
  const shortInstruction = instruction.replace(/^Return\s+/i, '').toLowerCase();

  return shortInstruction ? `${part.returnable} - ${shortInstruction}` : part.returnable;
};

export const nextPartId = (parts: PartEntry[]): number => Math.max(0, ...parts.map((entry) => entry.id)) + 1;

// Converts the API-shaped `additional_part_fields` config into the checkbox-form
// shape `PartsProviderInputs`/`PartsProviderSettings` render.
export const additionalPartFieldsToProviderInputs = (
  fields: AdditionalPartField[] = [],
): PartsProviderInputsValue => {
  const internalNumber = fields.find((field) => field.field_name === 'internal_number');

  return {
    rmaNumber: fields.some((field) => field.field_name === 'rma_number'),
    serialNumber: fields.some((field) => field.field_name === 'serial_number'),
    internalNumber: Boolean(internalNumber),
    internalNumberAlias: internalNumber?.field_label === 'Internal Number'
      ? ''
      : internalNumber?.field_label ?? '',
  };
};

// Inverse of `additionalPartFieldsToProviderInputs` — builds the API-shaped
// `additional_part_fields` config from the checkbox-form values.
export const providerInputsToAdditionalPartFields = (
  inputs: PartsProviderInputsValue,
): AdditionalPartField[] => {
  const fields = [] as AdditionalPartField[];

  if (inputs.rmaNumber) {
    fields.push({ field_name: 'rma_number', field_label: 'RMA Number' } as AdditionalPartField);
  }
  if (inputs.serialNumber) {
    fields.push({ field_name: 'serial_number', field_label: 'Serial Number' } as AdditionalPartField);
  }
  if (inputs.internalNumber) {
    fields.push({
      field_name: 'internal_number',
      field_label: inputs.internalNumberAlias.trim() || 'Internal Number',
    } as AdditionalPartField);
  }

  return fields;
};

export const convertPartDataToV1Payload = (
  newPart: Part,
  partUsageData: PartUsage,
): PartV3 => {
  const isReturnable = newPart?.returnable === 'Yes';
  const partData = {
    external_id: newPart.externalId,
    description: newPart.description,
    external_number: newPart.externalNumber,
    return_policy: {
      is_returnable: isReturnable,
      ...(newPart?.returnInstruction && {
        return_instruction: newPart.returnInstruction,
      }),
    },
    note: partUsageData?.note || '',
    incoming_tracking: {},
    return_tracking: {},
  } as PartV3;

  if (partUsageData?.usageCode) {
    partData.usage_code = {
      name: partUsageData.usageCode,
    };
  }

  if (partUsageData?.additionalPartValues) {
    partData.additional_part_values = partUsageData.additionalPartValues;
  }

  if (newPart?.trackingNumber) {
    partData.incoming_tracking = {
      tracking_id: newPart.trackingNumber,
      carrier_name: newPart?.carrier === 'other' ?
        newPart?.carrierOther : CARRIERS.find((carrier) => carrier.value === newPart?.carrier)?.label || '',
    };
  }

  const customerRetainedInt = parseInt(partUsageData?.customerRetained, 10) || 0;
  if (!customerRetainedInt && partUsageData?.returnTracking) {
    partData.return_tracking = {
      tracking_id: partUsageData.returnTracking || '',
      carrier_name:
        partUsageData.returnCarrier === 'other' ?
          partUsageData.returnCarrierOtherName : CARRIERS.find((carrier) => carrier.value === partUsageData?.returnCarrier)?.label || '',
      not_returned_reason: null,
    };
  } else {
    partData.return_tracking = {
      tracking_id: '',
      carrier_name: '',
      not_returned_reason: customerRetainedInt === 1 ? 'Customer retained' : null,
    };
  }
  return partData;
};
